import Ember from 'ember';
import { restartableTask, task } from 'ember-concurrency';
import moment from 'moment-timezone';
import { IBvTableColumn } from 'volta/components/bv-table';
// @ts-ignore
import ActionCell from 'volta/components/cell/action-cell';
// @ts-ignore
import FormatCell from 'volta/components/cell/format-cell';
// @ts-ignore
import IconCell from 'volta/components/cell/icon-cell';
// @ts-ignore
import PlanningPriorityCell from 'volta/components/cell/planning-priority-cell';
// @ts-ignore
import ReorderQtyEditorCell from 'volta/components/cell/reorder-qty-editor-cell';
// @ts-ignore
import TaskCell from 'volta/components/cell/task-cell';
import { heatmap as dafHeatMap } from 'volta/helpers/heatmap';
import PlanningModel, {
  Commands,
  PlanningEvents,
  PlanningResourceName,
  Queries
} from 'volta/models/planning';
import PlanningOrder from 'volta/models/planning-order';
import SkuModel, { SkuResourceName } from 'volta/models/sku';
import { TaskListChangeEvents } from 'volta/models/task';
import TaskService from 'volta/services/task-service';
import { SingleResourceDoc } from 'volta/utils/api/jsonapi-types';
import { defaultErrorHandler, TError } from 'volta/utils/error-utils';
import { eqFilter } from 'volta/utils/filters-utils';
import { classNames, getBufferZoneCriticality } from 'volta/utils/style-utils';

import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type IntlService from 'ember-intl/services/intl';
import type { IDashboardItem } from 'volta/models/types/app-features';
import type {
  IExecutionProjectionTs,
  IExecutionProjectionTsItem,
  IPlanningProjectionTs,
  IPlanningProjectionTsItem,
  IPlanningTs,
  IProjectionAndHistory,
  IProjectionTimeSeries
} from 'volta/models/types/planning';
import type AppSettings from 'volta/services/app-settings';
import type AuthorizationService from 'volta/services/authorization';
import type BvFlashService from 'volta/services/bv-flash';
import type BvMetricsService from 'volta/services/bv-metrics';
import type EventStreamService from 'volta/services/event-stream';
import type LabelService from 'volta/services/label-service';
import type PlanningOrderApi from 'volta/services/planning-order-api';
import type { TSkuDetailModalTab } from 'volta/services/sku-modal-service';
import type StoreService from 'volta/services/store';
import type LocalizationService from 'volta/services/localization';
import type { IBvTableBaseCellApi } from 'volta/components/cell/base-cell';
import SkuTask from 'volta/models/sku-task';
interface IArgs {
  skuId?: string;
  inModal?: boolean;
  sku?: SkuModel;
  switchTab?: (tab: TSkuDetailModalTab) => void;
  showOpenOrders?: (m: { dueDate: string }) => void;
}

interface IPlanningDashboardItem extends IDashboardItem {
  onClick?: (self: SkuDetailsPlanning) => void;
}

const DASHBOARD: Record<string, Partial<IPlanningDashboardItem>> = {
  onHand: {
    onClick: (self) => self && self.args.switchTab?.('stock')
  }
};

export interface IProjectionItem {
  key: string;
  projectionDate: string;
  planning: IPlanningProjectionTsItem;
  execution: IExecutionProjectionTsItem;
}

export default class SkuDetailsPlanning extends Component<IArgs> {
  // Services
  // ~~~~

  @service store!: StoreService;
  @service intl!: IntlService;
  @service localization!: LocalizationService;
  @service labelService!: LabelService;
  @service eventStream!: EventStreamService;
  @service bvFlash!: BvFlashService;
  @service bvMetrics!: BvMetricsService;
  @service authorization!: AuthorizationService;
  @service appSettings!: AppSettings;
  @service planningOrderApi!: PlanningOrderApi;
  @service taskService!: TaskService;

  // Properties
  // ~~~~

  @tracked planning?: PlanningModel;
  @tracked projectionSorts = [{ valuePath: 'projectionDate', isAscending: true }];
  @tracked selectedItems: IProjectionItem[] = [];
  @tracked projection?: IProjectionTimeSeries;
  @tracked history?: IPlanningTs;
  @tracked emptyRowsVisible = false;
  @tracked notCalculated = false;
  @tracked isCalculating = false;
  @tracked showResetManualReceptionsConfirm = false;
  @tracked scrollToDate?: string = undefined;
  @tracked planningTasks: SkuTask[] = [];
  @tracked selectedCell?: { key: string; start: Date; end: Date };
  @tracked idForFirstItem?: string;
  resourceName = PlanningResourceName;
  skuResourceName = SkuResourceName;

  // Computed Properties
  // ~~~~
  //

  get dashboardConfig() {
    return (this.appSettings.features?.planning.dashboard ?? []).map((item) => {
      return {
        ...item,
        ...(DASHBOARD[item.key as keyof typeof DASHBOARD] ?? {})
      };
    });
  }

  get reorderQty() {
    return this.planning?.reorderQty;
  }

  @cached
  get projectionAsArray() {
    if (!this.projection) {
      return [];
    }
    const { projectionDate = [], planning, execution } = this.projection;

    return projectionDate.map((date: string, index: number) => {
      const obj: IProjectionItem = {
        key: moment.utc(date).format('YYYYMMDD'),
        projectionDate: date,
        planning: {} as IPlanningProjectionTsItem,
        execution: {} as IExecutionProjectionTsItem
      };
      for (const key in planning) {
        if (Object.prototype.hasOwnProperty.call(planning, key)) {
          const value = planning[key as keyof IPlanningProjectionTs]?.[index];
          if (value) {
            obj.planning[key as keyof IPlanningProjectionTs] = value as never;
          }
        }
      }
      for (const key in execution) {
        if (Object.prototype.hasOwnProperty.call(execution, key)) {
          obj.execution[key as keyof IExecutionProjectionTs] = execution[
            key as keyof IExecutionProjectionTs
          ][index] as never;
        }
      }

      return obj;
    });
  }

  get projectionHasManualReceptions() {
    const supplies = this.projection?.planning?.supplyForecast ?? [];
    return supplies.some((v: number) => v > 0);
  }

  get filteredProjection() {
    const { emptyRowsVisible } = this;
    return this.projectionAsArray.filter((item: IProjectionItem) => {
      const isEmpty =
        (item.planning?.demand ?? 0) === 0 &&
        (item.planning?.supply ?? 0) === 0 &&
        (item.planning?.currentDemand ?? 0) === 0 &&
        (item.planning?.reorder ?? 0) === 0 &&
        (item.planning?.reception ?? 0) === 0 &&
        (item.planning?.supplyForecast ?? 0) === 0;
      return !(!emptyRowsVisible && isEmpty);
    });
  }

  get hiddenProjectionCount() {
    return (this.projection?.projectionDate?.length ?? 0) - this.filteredProjection.length;
  }

  @sort('filteredProjection', 'projectionSortProperties')
  sortedProjection!: IProjectionItem[];

  get unitMeasureLabel() {
    return this.planning?.unitMeasureLabel;
  }

  get projectionSortProperties() {
    return this.projectionSorts.map((s) => {
      return `${s.valuePath}:${s.isAscending ? 'asc' : 'desc'}`;
    });
  }

  get manualReceptionsActivated() {
    return (
      (this.planning?.isBuffered && this.appSettings.features?.planning.ddmrpManualReceptions) ||
      (this.planning?.isMRP && this.appSettings.features?.planning.mrpManualReceptions)
    );
  }

  get defaultProjectionColumns(): Record<string, IBvTableColumn | undefined> {
    const { intl, authorization, unitMeasureLabel } = this;
    return {
      demandSpike: {
        columnName: '',
        valuePath: 'planning.demandSpike',
        width: 80,
        textAlign: 'center',
        cellComponent: IconCell,
        isResizable: false,
        format: (model: IProjectionItem) => {
          const {
            planning: { demandSpike }
          } = model;
          return {
            visible: demandSpike && Math.abs(demandSpike) > 0,
            glyph: 'flash',
            color: 'warning',
            size: 'sm',
            tooltip: demandSpike ? intl.formatNumber(Math.abs(demandSpike)) : null
          };
        }
      },
      week: {
        columnName: intl.t('week'),
        valuePath: 'week',
        width: 50,
        isReorderable: true,
        isResizable: false,
        cellComponent: FormatCell,
        textAlign: 'right',
        classNames: 'u-text-mono',
        format: (model: IProjectionItem) => {
          const isCurrentWeek = moment
            .utc()
            .isSameOrAfter(moment.utc(model.projectionDate), 'week');
          const inFirstLT = moment
            .utc(model.projectionDate)
            .isSameOrBefore(this.planning?.dueDate as Date);
          return {
            value: model.projectionDate,
            dataType: 'date',
            format: 'w',
            cellClasses: classNames([isCurrentWeek && 'u-text-bolder', inFirstLT && 'u-color-blue'])
          };
        }
      },
      projectionDate: {
        columnName: intl.t('inventoryDate'),
        valuePath: 'projectionDate',
        width: 110,
        isReorderable: true,
        isResizable: false,
        cellComponent: FormatCell,
        format: (model: IProjectionItem) => {
          const isCurrentDay = moment.utc().isSameOrAfter(moment.utc(model.projectionDate), 'days');
          const inFirstLT = moment
            .utc(model.projectionDate)
            .isSameOrBefore(this.planning?.dueDate as Date);
          const cellClasses = classNames([
            isCurrentDay && 'u-text-bolder',
            inFirstLT && 'u-color-blue'
          ]);
          return {
            value: model.projectionDate,
            dataType: 'date',
            cellClasses
          };
        }
      },
      demand: {
        columnName: intl.t('demand'),
        valuePath: 'planning.demand',
        width: 80,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: IProjectionItem) => {
          const { selectedCell, planning } = this;
          const { projectionDate, planning: modelPlanning } = model;
          const isCurrentDay = moment.utc().isSameOrAfter(moment.utc(projectionDate), 'days');
          const isSelected =
            'planning.demand' === selectedCell?.key &&
            planning?.isMRP &&
            moment
              .utc(projectionDate)
              .isBetween(selectedCell?.start, selectedCell?.end, 'days', '[]');
          const cellClasses = classNames([
            isCurrentDay && 'u-color-success u-text-bold',
            isSelected && 'u-bg-surface-success-subdued'
          ]);

          return {
            value: Math.abs(modelPlanning.demand ?? 0),
            dataType: 'number',
            cellClasses,
            onClick: (api: IBvTableBaseCellApi<IProjectionItem>) => {
              this.selectedCell = api.model.projectionDate
                ? {
                    key: api.column.valuePath,
                    start: moment.utc(api.model.projectionDate).toDate(),
                    end: moment.utc(modelPlanning.demandHorizonDate).toDate()
                  }
                : undefined;
            }
          };
        }
      },
      supply: {
        columnName: intl.t('supply'),
        valuePath: 'planning.supply',
        width: 80,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: IProjectionItem) => {
          return {
            value: model.planning.supply,
            dataType: 'number'
          };
        }
      },
      currentDemand: {
        columnName: intl.t('simulatedDemand'),
        valuePath: 'planning.currentDemand',
        width: 75,
        textAlign: 'right',
        classNames: 'u-text-mono',
        isReorderable: true,
        isResizable: true,
        cellComponent: IconCell,
        format: (model: IProjectionItem) => {
          const {
            planning: { currentDemand, daf }
          } = model;
          const style = isPresent(daf)
            ? dafHeatMap(['var(--bv-color-teal-4-rgb)', daf])
            : undefined;
          return {
            visible: true,
            style,
            glyph: isPresent(daf)
              ? daf > 1
                ? 'caret-up-outline'
                : daf < 1
                ? 'caret-down-outline'
                : undefined
              : undefined,
            color: 'teal-6',
            classNames: 'u-border-radius u-text-mono',
            tooltip: daf
              ? `${intl.t('withDaf')}: ${intl.formatNumber(daf, { style: 'percent' })}`
              : undefined,
            size: 'sm',
            content: intl.formatNumber(Math.abs(currentDemand ?? 0))
          };
        }
      },
      reorder: {
        columnName: intl.t('reorderQty'),
        valuePath: 'planning.reorder',
        width: 80,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: IProjectionItem) => {
          return {
            value: model.planning.reorder,
            dataType: 'number',
            tooltipContent: model.planning.reorder > 0 && [
              {
                text: `${moment.utc(model.planning.dueDate).format('L')}`,
                subtext: this.intl.t('dueDate')
              }
            ],
            onClick: (api: IBvTableBaseCellApi<IProjectionItem>) => {
              const id = moment.utc(model.planning.dueDate).format('YYYYMMDD');
              api.scrollToRow(id);
            }
          };
        }
      },
      reception: this.manualReceptionsActivated
        ? {
            columnName: intl.t('receptionQtyShort'),
            valuePath: 'planning.reception',
            width: 80,
            textAlign: 'right',
            isReorderable: true,
            isResizable: true,
            cellComponent: ReorderQtyEditorCell,
            format: (model: IProjectionItem) => {
              const { planning, projectionDate } = model;
              const receptionFormatted = planning.reception
                ? intl.formatNumber(planning.reception)
                : undefined;

              return {
                disabled: authorization.cannot(
                  PlanningResourceName.plural,
                  Commands.SetManualReceptions
                ),
                qty: planning.supplyForecast,
                qtyDisplay: receptionFormatted,
                label: intl.t('supplyForecast'),
                helpText: `${intl.t(
                  'receptionQtyShort'
                )} — ${receptionFormatted} ${unitMeasureLabel}`,
                unitMeasure: unitMeasureLabel,
                moq: this.planning?.skuInfo.moq,
                orderMultiple: this.planning?.skuInfo.orderMultiple,
                onSubmit: async (qty: number) => {
                  return this.onReceptionEditorSubmit(projectionDate, qty);
                },
                classNames:
                  'u-text-mono' + (this.projectionHasManualReceptions ? ' u-color-severe' : '')
              };
            }
          }
        : {
            columnName: intl.t('receptionQtyShort'),
            valuePath: 'planning.reception',
            width: 80,
            textAlign: 'right',
            isReorderable: true,
            isResizable: true,
            cellComponent: FormatCell,
            format: (model: IProjectionItem) => {
              const reorderModel = (
                model.planning.reception
                  ? this.projectionAsArray.filter(
                      (p) =>
                        p.planning.dueDate === model.projectionDate &&
                        p.planning.reorder === model.planning.reception
                    ).lastObject ?? this.projectionAsArray.firstObject
                  : undefined
              ) as IProjectionItem | undefined;

              const hasReorder =
                reorderModel?.planning?.reorder &&
                reorderModel.projectionDate !== model.projectionDate;

              return {
                value: model.planning.reception,
                dataType: 'number',
                tooltipContent: hasReorder &&
                  reorderModel?.projectionDate && [
                    {
                      text: `${this.intl.formatDate(reorderModel.projectionDate, {
                        timeZone: 'UTC'
                      })}`,
                      subtext: this.intl.t('reorderDate')
                    }
                  ],
                onClick: (api: IBvTableBaseCellApi<IProjectionItem>) => {
                  if (hasReorder) {
                    const id = moment
                      .utc(reorderModel?.projectionDate as string)
                      .format('YYYYMMDD');
                    api.scrollToRow(id);
                  }
                }
              };
            }
          },
      onHandSimulated: {
        columnName: intl.t('onHandSimulated'),
        valuePath: 'planning.onHandSimulated',
        width: 85,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: IProjectionItem) => {
          return {
            value: model.planning.onHandSimulated ?? 0,
            dataType: 'number',
            cellClasses: classNames([model.planning.onHandSimulated < 0 && 'u-color-red'])
          };
        }
      },
      adu: {
        columnName: intl.t('adu'),
        valuePath: 'planning.adu',
        width: 80,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: IProjectionItem) => {
          return {
            value: Math.abs(model.planning.adu ?? 0),
            dataType: 'number',
            cellClasses: moment.utc().isSameOrAfter(moment.utc(model.projectionDate))
              ? 'u-color-success u-text-bold'
              : ''
          };
        }
      },
      netFlow: {
        columnName: intl.t(this.planning?.isBuffered ? 'netFlow' : 'netNeeds'),
        valuePath: 'planning.netFlow',
        width: 85,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: IProjectionItem) => {
          return {
            value: model.planning.netFlow ?? 0,
            dataType: 'number',
            cellClasses: classNames([model.planning.netFlow < 0 && 'u-color-red'])
          };
        }
      },
      bufferPenetration: {
        columnName: intl.t('simulatedBufferPriority'),
        valuePath: 'planning.bufferPenetration',
        width: 90,
        isReorderable: true,
        isResizable: true,
        cellComponent: PlanningPriorityCell,
        format: (model: IProjectionItem) => {
          return {
            priorityName: getBufferZoneCriticality(model.planning.bufferZone),
            bufferPenetration: model.planning.bufferPenetration,
            hasDemandSpike: model.planning.demandSpike > 0
          };
        }
      },
      openOrders: {
        columnName: intl.t('openOrders'),
        valuePath: 'openOrders',
        minWidth: 36,
        width: 36,
        isReorderable: true,
        isResizable: true,
        isSortable: false,
        cellComponent: ActionCell,
        format: (model: IProjectionItem) => {
          return {
            model: { dueDate: model.projectionDate },
            context: this,
            visible: true,
            action: (m: { dueDate: string }) => this.args.showOpenOrders?.(m),
            icon: 'open',
            size: 'sm',
            tooltip: intl.t('showOpenOrders')
          };
        }
      },
      planningTasks: {
        columnName: intl.t('commentsPage.title'),
        valuePath: 'tasks',
        width: 20,
        minWidth: 20,
        maxWidth: 70,
        isReorderable: true,
        isResizable: true,
        isSortable: true,
        cellComponent: TaskCell,
        format: (model: IProjectionItem) => {
          return {
            model,
            context: this,
            toggleTasks: this.toggleTasks,
            tasks: this.planningTasks,
            isOpened: model.projectionDate === this.scrollToDate,
            inPage: !this.args.inModal,
            entityId: this.args.skuId,
            dueDate: model.projectionDate,
            taskType: 'PLANNING_TASK',
            resourceName: this.skuResourceName,
            onDraftTask: this.onDraftTask,
            onSaveTask: this.onSaveTask,
            onDeleteTask: this.onDeleteTask,
            onCloseTasks: this.onCloseTasks
          };
        }
      },
      simulatedBalance: {
        columnName: intl.t('simulatedBalance'),
        sortKey: 'execution.simulatedBalance',
        valuePath: 'execution.simulatedBalance',
        width: 85,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: IProjectionItem) => {
          return {
            value: model.execution.simulatedBalance ?? 0,
            dataType: 'number',
            cellClasses: classNames([model.execution.simulatedBalance < 0 && 'u-color-red'])
          };
        }
      }
    };
  }

  get projectionColumns() {
    const columnsConfig = this.appSettings.features?.planning.projectionColumns ?? [];
    const columns: IBvTableColumn[] = [];
    const { defaultProjectionColumns } = this;
    columnsConfig.forEach((config) => {
      const column = defaultProjectionColumns[config.key as string];
      if (column && config.enabled) {
        if (config.title) {
          column.columnName = this.localization.tOrElse(config.title) as string;
        }
        columns.push(column);
      }
    });

    return columns;
  }

  get bufferZoneClassName() {
    return this.planning && `buffer-zone-${this.planning.priorityName}`;
  }

  get chartData() {
    if (!this.history) {
      return undefined;
    }

    const data = Object.assign(
      {} as { simulatedBalance: number[]; currentDemand: number[] },
      this.history
    );

    if (this.projection) {
      const { projectionDate, planning, execution } = this.projection;
      const onHand = data.onHand.slice();
      data.inventoryDate = data.inventoryDate.concat(projectionDate.slice(1));
      data.availableStock = data.availableStock.concat(planning.netFlow.slice(1));
      data.bufferGreen = data.bufferGreen.concat(planning.bufferGreen.slice(1));
      data.bufferYellow = data.bufferYellow.concat(planning.bufferYellow.slice(1));
      data.bufferRedBase = data.bufferRedBase.concat(planning.bufferRedBase.slice(1));
      data.bufferRedSafety = data.bufferRedSafety.concat(planning.bufferRedSafety.slice(1));
      data.bufferTOG = data.bufferTOG.concat(planning.bufferTOG.slice(1));
      data.bufferTOY = data.bufferTOY.concat(planning.bufferTOY.slice(1));
      data.bufferTOR = data.bufferTOR.concat(planning.bufferTOR.slice(1));
      data.bufferZone = data.bufferZone.concat(planning.bufferZone.slice(1));
      data.bufferPenetration = data.bufferPenetration.concat(planning.bufferPenetration.slice(1));
      data.onHand = onHand.concat(planning.onHandSimulated.slice(1));
      data.avgOnHand = data.avgOnHand.concat(planning.avgOnHand.slice(1));
      data.avgOnHandRange = data.avgOnHandRange.concat(planning.avgOnHandRange.slice(1));
      data.openSupplyQty = data.openSupplyQty.concat(planning.openSupply.slice(1));
      data.simulatedBalance = onHand.concat(execution.simulatedBalance.slice(1));
      data.demandQty = data.demandQty.concat(planning.currentDemand.slice(1));
      data.adu = data.adu.concat(planning.adu.slice(1));
      data.daf = data.daf.concat(planning.daf.slice(1));
      data.reorderQty = data.reorderQty.concat(planning.reorder.slice(1));
    }

    return data;
  }

  // Lifecycle Hooks
  // ~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.eventStream.addHandler(
      PlanningResourceName.plural,
      [PlanningEvents.PlanningsCalculationDone],
      this.onPlanningsCalculationDone,
      this
    );
    this.eventStream.addHandler(
      PlanningResourceName.plural,
      [PlanningEvents.PlanningsCalculationStarted],
      this.onPlanningsCalculationStarted,
      this
    );
    this.eventStream.addHandler(
      this.skuResourceName.plural,
      TaskListChangeEvents,
      this.handleTaskEvent,
      this
    );
  }

  willDestroy() {
    this.eventStream.removeHandlers(this);
  }

  // Actions
  // ~~~~~

  @action
  async prepareOrder() {
    this.bvMetrics.trackAction(this, 'prepareOrder');
    if (!this.planning) {
      return;
    }

    const planningIds = [this.planning.id];
    try {
      const response = await PlanningOrder.createDraftOrder({
        planningIds
      });
      this.bvFlash.success(this.intl.t('planningsPage.createDraftOrderSuccess'));
      return response;
    } catch (error) {
      return this.bvFlash.apiError(error);
    }
  }

  @action
  toggleEmptyRowsVisibility() {
    this.emptyRowsVisible = !this.emptyRowsVisible;
  }

  @action
  toggleTasks(dueDate?: string) {
    this.scrollToDate = dueDate ?? '';
  }

  @action
  onDraftTask(task: SkuTask) {
    this.planningTasks = [...this.planningTasks, task];
  }

  @action
  async onSaveTask(task?: SkuTask) {
    this.planningTasks = this.planningTasks
      .filter((t) => t?.id && t.id !== task?.id)
      .concat(task?.id ? [task] : []);
  }

  @action
  async onDeleteTask(task?: SkuTask) {
    const { skuId } = this.args;

    this.planningTasks = this.planningTasks.filter((t) => t.id !== task?.id);
    if (skuId && task?.id) {
      return this.planningTask.perform(skuId);
    }
  }

  @action
  async onCloseTasks(task?: SkuTask) {
    if (!task) {
      return;
    }
    if (task.newWithoutComment) {
      return this.onDeleteTask(task);
    } else {
      return this.taskService.saveTask.perform(task);
    }
  }

  @action
  onTaskUpdated(tasks: SkuTask[]) {
    this.planningTasks = tasks;
  }

  @action
  onReceptionEditorSubmit(date: string, qty: number) {
    if (this.args.skuId) {
      this.setManualReceptionsTask.perform(this.args.skuId, [{ date, qty }]);
    }
  }

  @action
  handleToggleReceptionMode(isManual: boolean) {
    this.bvMetrics.trackAction(this, 'handleToggleReceptionMode');
    if (this.args.skuId) {
      if (isManual) {
        this.toggleManualModeTask.perform(this.args.skuId, true);
      } else {
        this.showResetManualReceptionsConfirm = true;
      }
    }
  }

  @action
  resetManualReceptions() {
    this.bvMetrics.trackAction(this, 'resetManualReceptions');
    if (this.args.skuId) {
      this.toggleManualModeTask.perform(this.args.skuId, false);
    }
  }

  @action
  async handleDraftProjections() {
    this.bvMetrics.trackAction(this, 'handleDraftProjections');

    const { skuId } = this.args;
    if (skuId) {
      const dates = this.selectedItems.map((item) => item.projectionDate);
      try {
        const response = await this.planningOrderApi.draftPlanningProjectionsTask.perform(
          skuId,
          dates
        );
        if (response?.length) {
          this.bvFlash.success(
            this.intl.t('poWorkbench.draftManyPloSuccess', { count: response?.length })
          );
        } else {
          this.bvFlash.warning(this.intl.t('poWorkbench.draftManyPloWarning'));
        }

        this.selectedItems = [];
        return response;
      } catch (error) {
        return this.bvFlash.error(error);
      }
    }
  }

  @action
  handleSkuIdChange() {
    this.refreshData.perform();
  }

  // Helper Functions
  // ~~~~~

  onPlanningsCalculationDone(payload: { skuIds: string[] }) {
    if (this.isDestroying || this.isDestroyed || !this.args.skuId) {
      return;
    }
    const skuIds = payload.skuIds ?? [];

    if (
      (this.planning && skuIds.includes(this.planning?.skuInfo.skuId!)) ||
      skuIds.includes(this.args.skuId)
    ) {
      this.refreshData.perform();
      this.isCalculating = false;
    }
  }

  onPlanningsCalculationStarted(payload: { skuIds: string[] }) {
    if (this.isDestroying || this.isDestroyed || !this.args.skuId) {
      return;
    }
    const skuIds = payload.skuIds ?? [];
    if (
      (this.planning && skuIds.includes(this.planning?.skuInfo.skuId!)) ||
      skuIds.includes(this.args.skuId)
    ) {
      this.isCalculating = true;
    }
  }

  async handleTaskEvent(payload: SingleResourceDoc) {
    const { linkedEntityId, taskType } = payload.data.attributes ?? {};
    const skuId = this.planning?.skuInfo.skuId;
    if (taskType === 'PLANNING_TASK' && skuId && skuId === linkedEntityId) {
      await this.planningTask.perform(skuId);
    }
  }

  refreshData = task(async () => {
    const { skuId } = this.args;
    try {
      if (skuId) {
        await this.planningTask.perform(skuId);
        await this.projectionAndHistoryTask.perform(skuId);
        this.planningTasks = (await this.taskService.fetchPlanningTasks.perform(
          skuId
        )) as SkuTask[];
      }
    } catch (error) {
      defaultErrorHandler(error);
    }
  });

  toggleManualModeTask = task(async (skuId: string, manual: boolean) => {
    try {
      const response: { skuId: string; count: number } = await PlanningModel.setReceptionsMode({
        skuId,
        manual
      });
      this.showResetManualReceptionsConfirm = false;
      return response;
    } catch (error) {
      return this._onFailure(error);
    }
  });

  setManualReceptionsTask = task(
    async (skuId: string, receptions: Array<{ date: string; qty: number }>) => {
      try {
        if (!skuId || !receptions || !receptions.length) {
          return;
        }
        const response: { skuId: string; count: number } = await PlanningModel.setManualReceptions({
          skuId,
          receptions
        });
        return response;
      } catch (error) {
        return this._onFailure(error);
      }
    }
  );

  generateTask = restartableTask(async (skuId: string) => {
    try {
      const date = moment.utc().format('YYYY-MM-DD');
      const result: {} = await PlanningModel.generateMany({
        date,
        skuIds: [skuId],
        warehouseIds: []
      });
      return result;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });

  planningTask = restartableTask(async (skuId: string) => {
    try {
      const query = {
        filter: {
          skuId: eqFilter(skuId)
        }
      };

      const plannings: Ember.Array<PlanningModel> = await this.store.query(
        PlanningResourceName.singular,
        query
      );
      const planning = plannings.firstObject;

      if (!isPresent(planning)) {
        this.notCalculated = true;
        return;
      }

      this.notCalculated = false;
      this.planning = planning;

      return planning;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });

  projectionAndHistoryTask = restartableTask(async (skuId: string) => {
    try {
      const projectionAndHistory: IProjectionAndHistory = await this.store.customQuery(
        PlanningResourceName.singular,
        Queries.Projection,
        undefined,
        {
          skuId
        }
      );
      const { history, projection } = projectionAndHistory;

      this.history = history;
      this.projection = projection;

      return projectionAndHistory;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });

  _onFailure(error: TError) {
    defaultErrorHandler(error);
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    this.bvFlash.apiError(error);
  }
}
